<script setup lang="ts">
import { watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'

const { t } = useI18n()
const { umsokn } = useUmsokn()

const props = defineProps<{
  disable?: boolean
}>()

// rules: must pick payment type
const rules = {
  umsokn: {
    tegundGreiðslu: { required }
  }
}
const v$ = useVuelidate(rules, { umsokn })

// format helpers: thousands separator with dots
function formatNumber(val: string) {
  if (!val) return ''
  return val
    .replace(/\D/g, '')
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.')
}

watch(
  () => umsokn.value?.upphaedManudi,
  val => {
    if (umsokn.value) umsokn.value.upphaedManudi = formatNumber(val || '')
  }
)

watch(
  () => umsokn.value?.upphaed,
  val => {
    if (umsokn.value) umsokn.value.upphaed = formatNumber(val || '')
  }
)

// validate hook for ApplicationBase
async function validate(): Promise<boolean> {
  await v$.value.$validate()
  return !v$.value.$error
}
defineExpose({ validate })
</script>

<template>
  <v-col v-if="umsokn">
    <v-row>
      <v-col>
        <h2 class="forms__header">
          {{ t('utgreidsla_umsokn_greidslutilhogun_header') }}
        </h2>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <p class="font-bold">
          {{ t('utgreidsla_umsokn_greidslutilhogun_header2') }}
        </p>
      </v-col>
    </v-row>

    <!-- Eingreiðsla -->
    <v-radio-group
      v-model="umsokn.tegundGreiðslu"
      :readonly="props.disable"
      hide-details
      :error="v$.umsokn.tegundGreiðslu.$error"
      :error-messages="v$.umsokn.tegundGreiðslu.$error ? [t('common.val_required')] : []"
    >
      <v-radio
        :label="t('utgreidsla_umsokn_greidslutilhogun_eingreidsla')"
        :value="'eingreidsla'"
        @change="
          umsokn.upphaedManudi = null;
          umsokn.upphaed = null;
          umsokn.greida = null;
          umsokn.fjoldiManada = null;
        "
      />
      <!-- conditional inner radios when eingreiðsla chosen -->
      <div v-if="umsokn.tegundGreiðslu === 'eingreidsla'" class="pl-4">
        <v-radio-group v-model="umsokn.greida" :readonly="props.disable" hide-details>
          <v-radio
            :label="t('utgreidsla_umsokn_greidslutilhogun_greida_ut_allt')"
            :value="'allt'"
          />
          <v-radio
            :label="t('utgreidsla_umsokn_greidslutilhogun_greida_ut_hluta')"
            :value="'hluta'"
          />
        </v-radio-group>
        <v-text-field
          v-if="umsokn.greida === 'hluta'"
          v-model="umsokn.upphaed"
          :readonly="props.disable"
          :label="t('utgreidsla_umsokn_greidslutilhogun_upphaed')"
        />
      </div>

      <!-- Mánaðarleg föst -->
      <v-radio
        :label="t('utgreidsla_umsokn_greidslutilhogun_manadarleg_fost')"
        :value="'manadarleg_fost'"
        @change="
          umsokn.upphaedManudi = null;
          umsokn.upphaed = null;
          umsokn.greida = null;
          umsokn.fjoldiManada = null;
        "
      />
      <div v-if="umsokn.tegundGreiðslu === 'manadarleg_fost'" class="pl-4">
        <v-text-field
          v-model="umsokn.upphaedManudi"
          :readonly="props.disable"
          :label="t('utgreidsla_umsokn_greidslutilhogun_upphaed_manudi')"
        />
        <v-text-field
          v-model="umsokn.fjoldiManada"
          :readonly="props.disable"
          :label="t('utgreidsla_umsokn_greidslutilhogun_fjoldi_manuda')"
          type="number"
        />
      </div>

      <!-- Mánaðarleg dreifð -->
      <v-radio
        :label="t('utgreidsla_umsokn_greidslutilhogun_manadarleg_dreifa')"
        :value="'manadarleg_dreifa'"
        @change="
          umsokn.upphaedManudi = null;
          umsokn.upphaed = null;
          umsokn.greida = null;
          umsokn.fjoldiManada = null;
        "
      />
      <div v-if="umsokn.tegundGreiðslu === 'manadarleg_dreifa'" class="pl-4">
        <v-text-field
          v-model="umsokn.fjoldiManada"
          :readonly="props.disable"
          :label="t('utgreidsla_umsokn_greidslutilhogun_fjoldi_manuda')"
          type="number"
        />
      </div>
    </v-radio-group>
  </v-col>
</template>

<style scoped>
.m-b-40 { margin-bottom: 40px; }
.forms__header {
  color: #194458;
  font-weight: 900;
  font-size: 28px;
  line-height: 1.25;
  margin: 0 0 12px;
}
.font-bold {
  font-weight: bold;
}
</style>
